import { useState, FormEvent } from 'react';

export default function ContactForm() {
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    email: '',
    phone: '',
    employees: '',
    printVolume: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4uee319ncmb029djrcg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          company: '',
          name: '',
          email: '',
          phone: '',
          employees: '',
          printVolume: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            お問い合わせ
          </h2>
          <p className="text-xl text-gray-600">
            まずはお気軽にご相談ください。専門スタッフが最適なプランをご提案いたします。
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <form onSubmit={handleSubmit} data-readdy-form id="contact_form">
            <div className="space-y-6">
              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-gray-900 mb-2">
                  会社名 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  required
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors text-base"
                  placeholder="株式会社〇〇"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors text-base"
                    placeholder="山田 太郎"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors text-base"
                    placeholder="090-3716-1918"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  メールアドレス <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors text-base"
                  placeholder="example@company.co.jp"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="employees" className="block text-sm font-semibold text-gray-900 mb-2">
                    従業員数
                  </label>
                  <select
                    id="employees"
                    name="employees"
                    value={formData.employees}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors text-base cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    <option value="1-10">1〜10名</option>
                    <option value="11-50">11〜50名</option>
                    <option value="51-100">51〜100名</option>
                    <option value="101-500">101〜500名</option>
                    <option value="501+">501名以上</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="printVolume" className="block text-sm font-semibold text-gray-900 mb-2">
                    月間印刷枚数（予想）
                  </label>
                  <select
                    id="printVolume"
                    name="printVolume"
                    value={formData.printVolume}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors text-base cursor-pointer"
                  >
                    <option value="">選択してください</option>
                    <option value="0-500">〜500枚</option>
                    <option value="501-1000">501〜1,000枚</option>
                    <option value="1001-3000">1,001〜3,000枚</option>
                    <option value="3001-5000">3,001〜5,000枚</option>
                    <option value="5001+">5,001枚以上</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                  お問い合わせ内容
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  value={formData.message}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-600 focus:outline-none transition-colors text-base resize-none"
                  placeholder="ご質問やご要望などをご記入ください（500文字以内）"
                ></textarea>
                <p className="text-sm text-gray-500 mt-2">{formData.message.length}/500文字</p>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded">
                  <p className="text-green-700 font-medium">
                    お問い合わせありがとうございます。担当者より折り返しご連絡いたします。
                  </p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
                  <p className="text-red-700 font-medium">
                    送信に失敗しました。もう一度お試しください。
                  </p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-600 text-white py-4 rounded-full text-lg font-bold hover:bg-blue-700 transition-all disabled:bg-gray-400 disabled:cursor-not-allowed shadow-lg whitespace-nowrap"
              >
                {isSubmitting ? '送信中...' : '無料相談を申し込む'}
              </button>
            </div>
          </form>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <div className="flex flex-col md:flex-row gap-6 text-sm text-gray-600">
              <div className="flex items-start gap-3">
                <i className="ri-phone-line text-2xl text-blue-600 mt-1"></i>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">お電話でのお問い合わせ</p>
                  <p className="text-xl font-bold text-blue-600">090-3716-1918</p>
                  <p className="text-xs mt-1">受付時間：平日 9:00〜18:00</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-mail-line text-2xl text-blue-600 mt-1"></i>
                <div>
                  <p className="font-semibold text-gray-900 mb-1">メールでのお問い合わせ</p>
                  <p className="text-base font-semibold text-blue-600">info@it-support.club</p>
                  <p className="text-xs mt-1">担当者：大久保博</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
